import { Button, InputAdornment, ListItemIcon, Menu, MenuItem, styled, TextField } from '@mui/material';
import { CheckIcon, LayoutGridIcon, ListFilterIcon, ListIcon, SearchIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

interface StyledMenuItemProps {
  isActive: boolean;
}

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<StyledMenuItemProps>`
  display: flex;
  justify-content: space-between;
  background-color: ${(props) => (props.isActive ? '#F7FAFC' : 'transparent')};

  & > span {
    font-size: 14px;
    font-weight: ${(props) => (props.isActive ? 600 : 'normal')};
    color: #4a5568;
  }

  & .MuiListItemIcon-root {
    min-width: 24px;
    color: #4a5568;
  }
`;

interface ToolbarProps {
  onSort: (sortBy: string) => void;
  activeSort: string;
  viewOption: 'list' | 'grid';
  toggleView: () => void;
  onSearch: (searchQuery: string) => void;
}

function Toolbar({ onSort, activeSort, viewOption, toggleView, onSearch }: ToolbarProps) {
  const [sortByMenuAnchorEl, setSortByMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [sortByMenuOpen, setSortByMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  const handleSortOptionClick = useCallback(
    (sortByValue: string) => {
      onSort(sortByValue);
      setSortByMenuOpen(false);
    },
    [onSort],
  );

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchText(newSearchTerm);

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    const delay = 500;
    timeoutId.current = setTimeout(() => {
      onSearch(newSearchTerm);
    }, delay);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' }}>
      <TextField
        onChange={handleSearchInputChange}
        value={searchText}
        sx={{ width: 360 }}
        aria-label='Search'
        placeholder='Search...'
        size='small'
        slotProps={{
          input: {
            sx: { borderRadius: '8px', fontSize: '14px' },
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon size={16} />
              </InputAdornment>
            ),
          },
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        {viewOption === 'list' &&
          (activeSort === 'created_at:desc' ? (
            <span style={{ fontSize: '14px', color: '#718096', fontWeight: 500, marginRight: '26px' }}>
              Last created
            </span>
          ) : (
            <span style={{ fontSize: '14px', color: '#718096', fontWeight: 500, marginRight: '20px' }}>
              Last modified
            </span>
          ))}
        <Button sx={{ color: '#4a5568' }} onClick={toggleView}>
          {viewOption === 'grid' ? <ListIcon /> : <LayoutGridIcon />}
          <span style={{ fontSize: '14px', marginLeft: '6px', color: '#718096' }}>
            {viewOption === 'grid' ? 'List' : 'Grid'}
          </span>
        </Button>
        <Button
          sx={{ color: '#4a5568' }}
          aria-haspopup='true'
          aria-expanded={sortByMenuOpen ? 'true' : undefined}
          onClick={(event) => {
            setSortByMenuAnchorEl(event.currentTarget);
            setSortByMenuOpen(true);
          }}
        >
          <ListFilterIcon style={{ fontSize: '14px' }} />
          <span style={{ fontSize: '14px', marginLeft: '6px', color: '#718096' }}>Sort by</span>
        </Button>
      </div>
      <Menu
        slotProps={{
          paper: {
            style: {
              width: '24ch',
            },
          },
        }}
        anchorEl={sortByMenuAnchorEl}
        open={sortByMenuOpen}
        onClose={() => {
          setSortByMenuOpen(false);
          setSortByMenuAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <StyledMenuItem isActive={activeSort === 'name:asc'} onClick={() => handleSortOptionClick('name:asc')}>
          <span>Title (A-Z)</span>
          {activeSort === 'name:asc' && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
        </StyledMenuItem>
        <StyledMenuItem isActive={activeSort === 'name:desc'} onClick={() => handleSortOptionClick('name:desc')}>
          <span>Title (Z-A)</span>
          {activeSort === 'name:desc' && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
        </StyledMenuItem>
        <StyledMenuItem
          isActive={activeSort === 'updated_at:desc'}
          onClick={() => handleSortOptionClick('updated_at:desc')}
        >
          <span>Last modified</span>
          {activeSort === 'updated_at:desc' && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
        </StyledMenuItem>
        <StyledMenuItem
          isActive={activeSort === 'created_at:desc'}
          onClick={() => handleSortOptionClick('created_at:desc')}
        >
          <span>Last created</span>
          {activeSort === 'created_at:desc' && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
        </StyledMenuItem>
      </Menu>
    </div>
  );
}

export default Toolbar;
